/**
 * FahrzeugLabel — Farbige Labels für Fahrzeuge (Issue #577)
 * 11 Label-Typen mit Farbcodierung, sortiert nach Wichtigkeit.
 * Mehrfach-Labels pro Fahrzeug möglich.
 */

import type { ReactNode } from 'react';

export type FahrzeugLabelTyp =
  | 'aktiv_im_pool'
  | 'vermietet'
  | 'reserviert'
  | 'in_werkstatt'
  | 'bestellt'
  | 'stillgelegt'
  | 'verkaufs_empfehlung'
  | 'hu_faellig'
  | 'vertrag_laeuft_aus'
  | 'top_auslastung'
  | 'niedrige_auslastung';

interface LabelConfig {
  label: string;
  color: string;
  bg: string;
  priority: number;
}

const LABEL_CONFIG: Record<FahrzeugLabelTyp, LabelConfig> = {
  in_werkstatt:       { label: 'In Werkstatt',        color: '#EA580C', bg: '#FFEDD5', priority: 1 },
  hu_faellig:         { label: 'HU fällig',            color: '#B91C1C', bg: '#FEF08A', priority: 2 },
  vertrag_laeuft_aus: { label: 'Vertrag läuft aus',    color: '#C2410C', bg: '#FED7AA', priority: 3 },
  bestellt:           { label: 'Bestellt',             color: '#DC2626', bg: '#FEE2E2', priority: 4 },
  vermietet:          { label: 'Vermietet',            color: '#1D4ED8', bg: '#DBEAFE', priority: 5 },
  reserviert:         { label: 'Reserviert',           color: '#B45309', bg: '#FEF3C7', priority: 6 },
  verkaufs_empfehlung:{ label: 'Verkaufs-Empfehlung',  color: '#7C3AED', bg: '#EDE9FE', priority: 7 },
  top_auslastung:     { label: 'Top-Auslastung',       color: '#92400E', bg: '#FEF3C7', priority: 8 },
  aktiv_im_pool:      { label: 'Aktiv im Pool',         color: '#065F46', bg: '#D1FAE5', priority: 9 },
  niedrige_auslastung:{ label: 'Niedrige Auslastung',  color: '#6B7280', bg: '#F3F4F6', priority: 10 },
  stillgelegt:        { label: 'Stillgelegt',          color: '#9CA3AF', bg: '#F9FAFB', priority: 11 },
};

/** Leitet aus dem Fahrzeug-Status und weiteren Feldern automatisch Labels ab. */
export function deriveLabels(fahrzeug: {
  status: string;
  hu_naechste?: string | null;
  laufzeitende?: string | null;
}): FahrzeugLabelTyp[] {
  const labels: FahrzeugLabelTyp[] = [];
  const today = new Date();
  const in60Days = new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000);

  if (fahrzeug.status === 'aktiv')       labels.push('aktiv_im_pool');
  if (fahrzeug.status === 'vermietet')   labels.push('vermietet');
  if (fahrzeug.status === 'reserviert')  labels.push('reserviert');
  if (fahrzeug.status === 'werkstatt')   labels.push('in_werkstatt');
  if (fahrzeug.status === 'bestellt')    labels.push('bestellt');
  if (fahrzeug.status === 'stillgelegt') labels.push('stillgelegt');

  if (fahrzeug.hu_naechste) {
    const hu = new Date(fahrzeug.hu_naechste);
    if (hu <= in60Days) labels.push('hu_faellig');
  }

  if (fahrzeug.laufzeitende) {
    const ende = new Date(fahrzeug.laufzeitende);
    if (ende <= in60Days) labels.push('vertrag_laeuft_aus');
  }

  return sortByPriority(labels);
}

function sortByPriority(labels: FahrzeugLabelTyp[]): FahrzeugLabelTyp[] {
  return [...labels].sort((a, b) => LABEL_CONFIG[a].priority - LABEL_CONFIG[b].priority);
}

interface FahrzeugLabelProps {
  typ: FahrzeugLabelTyp;
  size?: 'sm' | 'xs';
}

export function FahrzeugLabel({ typ, size = 'xs' }: FahrzeugLabelProps): ReactNode {
  const cfg = LABEL_CONFIG[typ];
  if (!cfg) return null;
  const classes =
    size === 'sm'
      ? 'inline-flex items-center px-2 py-1 rounded-full text-[11px] font-bold'
      : 'inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold';
  return (
    <span
      data-testid="fahrzeug-label"
      className={classes}
      style={{ backgroundColor: cfg.bg, color: cfg.color }}
    >
      {cfg.label}
    </span>
  );
}

interface FahrzeugLabelGroupProps {
  labels: FahrzeugLabelTyp[];
  max?: number;
  size?: 'sm' | 'xs';
}

export function FahrzeugLabelGroup({ labels, max = 3, size = 'xs' }: FahrzeugLabelGroupProps): ReactNode {
  const sorted = sortByPriority(labels);
  const visible = sorted.slice(0, max);
  const overflow = sorted.length - max;
  return (
    <div className="flex flex-wrap gap-1">
      {visible.map(typ => (
        <FahrzeugLabel key={typ} typ={typ} size={size} />
      ))}
      {overflow > 0 && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-500">
          +{overflow}
        </span>
      )}
    </div>
  );
}
